/**
 * Transactions. Every line in the mirror for one entity, with its lineage.
 *
 * Two things this screen is careful about:
 *
 *  1. **It shows money; it never computes it.** `amount_cents` arrives as a string and goes
 *     through `formatMoney` and nothing else (CLAUDE.md 3.1). There is no total on this page
 *     because a total computed here would be a number a browser made up; sums live in SQL.
 *  2. **Every value on the lineage panel is verbatim.** CLAUDE.md 13: "where did this number come
 *     from?" is answered here, from the stored lineage columns and the classification's
 *     provenance, shown as stored. The only mapping this file does is turning a derivation plus
 *     two foreign keys into the four words a person reads ("Rule, approved"), and the panel shows
 *     the fields that mapping was made from right beside it.
 */

import { Fragment, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  api,
  type Transaction,
  type TransactionClassification,
  type TransactionStatusFilter,
  type TransactionsPage,
} from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { formatMoney } from '../../money.js';

const STATUS_TABS: { value: TransactionStatusFilter; label: string }[] = [
  { value: 'unclassified', label: 'Unclassified' },
  { value: 'classified', label: 'Classified' },
  { value: 'all', label: 'All' },
];

function isStatusFilter(value: string | null): value is TransactionStatusFilter {
  return value === 'unclassified' || value === 'classified' || value === 'all';
}

const COLUMNS = 8;

export function TransactionsPage(): ReactNode {
  const [params, setParams] = useSearchParams();
  const entityId = useEntityId();
  const entities = useEntities();
  const month = params.get('month') ?? '';
  const statusParam = params.get('status');
  const status: TransactionStatusFilter = isStatusFilter(statusParam) ? statusParam : 'all';
  const [expanded, setExpanded] = useState<string | null>(null);

  function setParam(key: string, value: string): void {
    setParams((prev) => {
      if (value) prev.set(key, value);
      else prev.delete(key);
      return prev;
    });
  }

  // One page per API call, appended. The cursor is the API's and is passed back untouched.
  const list = useInfiniteQuery({
    queryKey: ['finance', 'transactions', entityId, month, status],
    enabled: entityId !== null,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const query = new URLSearchParams({ entity: entityId ?? '', status });
      if (month) query.set('month', month);
      if (pageParam) query.set('cursor', pageParam);
      return api.get<TransactionsPage>(`/api/finance/transactions?${query.toString()}`);
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const rows = list.data?.pages.flatMap((page) => page.transactions) ?? [];
  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Transactions</h1>
          <p className="subtitle">
            Every line in the mirror, with its lineage. Click a line for where it came from.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
        <label>
          Month
          <input
            type="month"
            value={month}
            onChange={(e) => setParam('month', e.target.value)}
            aria-label="Month"
          />
        </label>
        <div className="tabs" role="tablist" aria-label="Classification status">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={status === tab.value}
              className={status === tab.value ? 'tab active' : 'tab'}
              onClick={() => setParam('status', tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {(list.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {list.isError && <p className="error">Could not load transactions: {list.error.message}</p>}

      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {list.data && rows.length === 0 && (
        <div className="card empty">
          <h2>No transactions for this entity and month.</h2>
          <p>Lines arrive here from the QuickBooks connector, one per company file.</p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <table className="table transactions">
            <thead>
              <tr>
                <th>Date</th>
                <th>Vendor</th>
                <th>Description</th>
                <th className="num">Amount</th>
                <th>QBO account</th>
                <th>Canonical line</th>
                <th>Status</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const open = expanded === t.id;
                return (
                  <Fragment key={t.id}>
                    <tr
                      className={open ? 'clickable expanded' : 'clickable'}
                      aria-expanded={open}
                      onClick={() => setExpanded(open ? null : t.id)}
                    >
                      <td className="nowrap">{t.txn_date}</td>
                      <td>{t.vendor_name ?? <span className="muted">—</span>}</td>
                      <td>
                        {t.description ?? <span className="muted">—</span>}
                        <DescriptionLine t={t} />
                      </td>
                      <td className="num">{formatMoney(t.amount_cents)}</td>
                      <td>{t.account_name ?? <span className="muted">—</span>}</td>
                      <td>
                        {t.canonical_name ?? <span className="muted">—</span>}
                        {t.canonical_key && (
                          <div className="muted small">
                            <code>{t.canonical_key}</code>
                          </div>
                        )}
                      </td>
                      <td>
                        <ClassificationStatus t={t} />
                      </td>
                      <td>
                        <Flags t={t} />
                      </td>
                    </tr>
                    {open && (
                      <tr className="lineage">
                        <td colSpan={COLUMNS}>
                          <LineagePanel t={t} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          <div className="actions table-foot">
            <span className="muted small">
              {rows.length} {rows.length === 1 ? 'line' : 'lines'} shown
              {list.hasNextPage ? ' — more available' : ''}
            </span>
            {list.hasNextPage && (
              <button
                type="button"
                className="secondary"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

/** Document type and number, and the property, under the description. */
function DescriptionLine({ t }: { t: Transaction }): ReactNode {
  const parts = [
    t.doc_type,
    t.doc_number ? `#${t.doc_number}` : null,
    t.doc_line_num === null ? null : `line ${t.doc_line_num}`,
    t.property_name,
  ].filter((part): part is string => Boolean(part));
  if (parts.length === 0) return null;
  return <div className="muted small">{parts.join(' · ')}</div>;
}

/**
 * The four words for how an approved classification came to be. Plan 8 decision 1: a person's
 * decision is `human_entered`; the rule or the model recommendation it came from is named by a
 * foreign key. The lineage panel shows those keys beside this label.
 */
export function derivationLabel(c: TransactionClassification): string {
  switch (c.derivation) {
    case 'ai_inferred':
      return 'AI, accepted';
    case 'human_entered':
      if (c.from_vendor_rule_id) return 'Rule, approved';
      if (c.from_ai_classification_id) return 'AI, modified';
      return 'Person';
    default:
      // `observed` or `calculated` on a classification would be unexpected; shown as stored.
      return c.derivation;
  }
}

/**
 * Where the classification stands in QuickBooks. `external_sync_status` is the stored fact; the
 * title is the sync-back's own sentence for the latest outcome, so "failed" and "drifted" say why
 * without a trip to the audit log (ADR-0007). `not_applicable` — the approval changed nothing —
 * shows nothing: there was never anything to send.
 */
function SyncChip({ c }: { c: TransactionClassification }): ReactNode {
  const status = c.external_sync_status;
  if (status === 'not_applicable') return null;
  const label =
    status === 'synced'
      ? 'In QuickBooks'
      : status === 'pending'
        ? 'QuickBooks: pending'
        : status === 'drifted'
          ? 'QuickBooks: drifted'
          : status === 'failed'
            ? 'QuickBooks: refused'
            : `QuickBooks: ${status}`;
  const tone = status === 'synced' ? '' : status === 'pending' ? ' chip-muted' : ' chip-warn';
  return (
    <div className="chips flags">
      <span
        className={`chip${tone}`}
        title={c.sync_detail ?? 'No sync-back has run for this row yet.'}
      >
        {label}
      </span>
    </div>
  );
}

function ClassificationStatus({ t }: { t: Transaction }): ReactNode {
  const c = t.classification;
  if (c && c.approval_status === 'approved') {
    return (
      <>
        <span className="status status-approved">Approved</span>
        <div className="muted small">
          {derivationLabel(c)}
          {c.approved_by_name ? ` · ${c.approved_by_name}` : ''}
        </div>
        <SyncChip c={c} />
      </>
    );
  }
  // A classification row in any other state is shown by its stored status, not relabelled.
  if (c) return <span className="status">{c.approval_status}</span>;
  if (t.ai_proposal) return <span className="status status-proposed">AI proposed</span>;
  if (t.rule_hit) {
    return t.rule_hit.is_conflict ? (
      <span className="status status-conflict">Rule conflict</span>
    ) : (
      <span className="status status-rule">Rule hit</span>
    );
  }
  return <span className="status">Unclassified</span>;
}

/**
 * The flags column. Each chip is a stored row somewhere, named by what stored it: a pair in
 * `derived.duplicate_candidate`, a `transfer_group_id`, a kind in `derived.anomaly_candidate`.
 * An anomaly chip shows the detector's own `kind` with its underscores opened out — no label and
 * no severity is invented here, because the kind is the whole of what the detector said.
 */
function Flags({ t }: { t: Transaction }): ReactNode {
  if (t.duplicate_candidates === 0 && !t.transfer_group_id && t.anomaly_kinds.length === 0) {
    return null;
  }
  return (
    <div className="chips flags">
      {t.duplicate_candidates > 0 && (
        <span className="chip chip-warn" title="Pairs in derived.duplicate_candidate">
          {t.duplicate_candidates} duplicate{' '}
          {t.duplicate_candidates === 1 ? 'candidate' : 'candidates'}
        </span>
      )}
      {t.transfer_group_id && (
        <span className="chip" title={`transfer_group_id ${t.transfer_group_id}`}>
          transfer
        </span>
      )}
      {t.anomaly_kinds.map((kind) => (
        <span
          key={kind}
          className="chip chip-anomaly"
          title={`derived.anomaly_candidate kind ${kind}`}
        >
          {kind.replaceAll('_', ' ')}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The lineage panel. Field names are the column names, on purpose: the panel is the answer to
// "where did this come from?", and an answer in the database's own words can be checked against
// the database.
// ---------------------------------------------------------------------------------------------

type Field = [name: string, value: string | number | boolean | null];

function Fields({ title, fields }: { title: string; fields: Field[] }): ReactNode {
  return (
    <section className="lineage-section">
      <h4>{title}</h4>
      <dl className="kv">
        {fields.map(([name, value]) => (
          <Fragment key={name}>
            <dt>{name}</dt>
            <dd>
              {value === null || value === '' ? (
                <span className="muted">—</span>
              ) : typeof value === 'string' ? (
                <code>{value}</code>
              ) : (
                <code>{String(value)}</code>
              )}
            </dd>
          </Fragment>
        ))}
      </dl>
    </section>
  );
}

/**
 * A name is what a person reads; the id is what they paste into a query. CLAUDE.md 13 asks for
 * both, so a resolved row shows `Repairs (7c3e…)` and an unresolved one shows the bare id.
 */
function withId(name: string | null, id: string | null): string | null {
  if (name === null) return id;
  return id === null ? name : `${name} (${id})`;
}

function LineagePanel({ t }: { t: Transaction }): ReactNode {
  const c = t.classification;
  return (
    <div className="lineage-panel" onClick={(e) => e.stopPropagation()} role="presentation">
      <Fields
        title="Where this line came from"
        fields={[
          ['id', t.id],
          ['source_system', t.source_system],
          ['source_record_id', t.source_record_id],
          ['raw_id', t.raw_id],
          ['ingested_at', t.ingested_at],
          ['updated_at', t.updated_at],
          ['derivation', t.derivation],
          ['approval_status', t.approval_status],
          ['transfer_group_id', t.transfer_group_id],
          ['vendor_id', t.vendor_id],
          ['account_id', t.account_id],
          ['property_id', t.property_id],
          ['memo', t.memo],
          ['currency', t.currency],
          ['anomaly_kinds', t.anomaly_kinds.join(', ')],
        ]}
      />
      {c && (
        <Fields
          title={`Classification — ${derivationLabel(c)}`}
          fields={[
            ['id', c.id],
            ['approval_status', c.approval_status],
            ['derivation', c.derivation],
            ['from_ai_classification_id', c.from_ai_classification_id],
            ['from_vendor_rule_id', c.from_vendor_rule_id],
            ['ai_run_id', c.ai_run_id],
            ['confidence', c.confidence],
            ['approved_by', c.approved_by_name ?? c.approved_by],
            ['approved_at', c.approved_at],
            ['external_sync_status', c.external_sync_status],
            ['sync_detail', c.sync_detail],
            ['sync_at', c.sync_at],
            ['account', withId(c.account_name, c.account_id)],
            ['category', c.category],
            ['subcategory', c.subcategory],
            ['property', withId(c.property_name, c.property_id)],
            ['is_capex', c.is_capex],
          ]}
        />
      )}
      {t.rule_hit && (
        <Fields
          title={t.rule_hit.is_conflict ? 'Rule conflict' : 'Rule hit'}
          fields={[
            ['is_conflict', t.rule_hit.is_conflict],
            ['canonical_key', t.rule_hit.canonical_key],
            ['rule_ids', t.rule_hit.rule_ids.join(', ')],
            ['property_id', t.rule_hit.property_id],
            ['is_capex', t.rule_hit.is_capex],
          ]}
        />
      )}
      {t.ai_proposal && (
        <Fields
          title="Pending AI proposal"
          fields={[
            ['approval_id', t.ai_proposal.id],
            ['title', t.ai_proposal.title],
            ['run_id', t.ai_proposal.run_id],
            ['created_at', t.ai_proposal.created_at],
          ]}
        />
      )}
    </div>
  );
}
